import { fg } from '@atlaskit/platform-feature-flags/fg';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import Button, { IconButton } from '@atlaskit/button/new';

import { cssMap } from '@atlaskit/css';

import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';

import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';

import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';

import Heading from '@atlaskit/heading/heading';

import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';

import Image from '@atlaskit/image/image';

import Link from '@atlaskit/link/link';

import ModalBody from '@atlaskit/modal-dialog/modal-body';

import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';

import ModalFooter from '@atlaskit/modal-dialog/modal-footer';

import ModalHeader from '@atlaskit/modal-dialog/modal-header';

import ModalTitle from '@atlaskit/modal-dialog/modal-title';

import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { Pressable } from '@atlaskit/primitives/compiled/pressable';

import { token } from '@atlaskit/tokens';

import { openLoomMeetingRecordings } from '@atlassian/loom-meeting-recordings';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import type { PlacementFlags } from '@post-office/browser--growth/confluence-side-nav';

import { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { requestLoomMeetingNudgeReminder } from '../../../reminder-event';

import { useLoomMeetingNudgeAnalytics } from '../../../analytics';

import AiNotesIcon from './assets/ai-notes.svg';

import { registeredContent } from './messages';

type MeetingNudgeBannerProps = {
	cloudId: string;
	handleOnDismissed: () => void;
	flags: PlacementFlags;
};

const DATA_PRIVACY_URL = 'https://www.atlassian.com/legal/privacy-policy';

const NOT_INTERESTED_FLAG_ID = 'loom-meeting-nudge-not-interested';

const getPostOfficeDismissEvent = (
	clickIdentifier: 'connect-calendar' | 'not-interested' | 'remind-me',
	buttonType: 'primary' | 'secondary' = 'secondary',
) => ({
	messageEventType: 'clicked' as const,
	clickedElement: {
		clickIdentifier,
		clickedElement: 'button' as const,
		buttonType,
	},
	action: { actionType: 'dismiss' as const },
	additionalMetadataFromFrontend: {},
});

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '100%',
	},
	action: {
		alignItems: 'center',
		backgroundColor: 'transparent',
		display: 'inline-flex',
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
		color: token('color.link'),
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	aiNotesIcon: {
		height: '40px',
		width: '40px',
	},
	privacyLinkContainer: {
		marginTop: token('space.150'),
	},
});

export const MeetingNudgeBanner = ({
	cloudId,
	handleOnDismissed,
	flags,
}: MeetingNudgeBannerProps): JSX.Element => {
	const intl = useIntl();
	const [isNotInterestedFlagOpen, setIsNotInterestedFlagOpen] = useState(false);
	const [isWhyModalOpen, setIsWhyModalOpen] = useState(false);
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { onCtaActioned, onDeclined } = usePostOfficeUserInteractions();
	const { fireCtaClicked, fireScreenSeen, fireFlagDismissed } =
		useLoomMeetingNudgeAnalytics('sidenav');
	const reminderConfirmation = {
		title: intl.formatMessage(registeredContent.confirmations.reminderTitle),
		description: intl.formatMessage(registeredContent.confirmations.reminderDescription),
	};
	const notInterestedConfirmation = {
		title: intl.formatMessage(registeredContent.confirmations.notInterestedTitle),
		description: intl.formatMessage(registeredContent.confirmations.notInterestedDescription),
		undo: intl.formatMessage(registeredContent.confirmations.notInterestedUndo),
	};

	useEffect(() => {
		if (isWhyModalOpen) {
			fireScreenSeen('whyAmISeeingThis');
		}
	}, [fireScreenSeen, isWhyModalOpen]);

	const finishNudgeFlow = () => {
		setIsNotInterestedFlagOpen(false);
		handleOnDismissed();
	};

	const handleConnectCalendar = () => {
		fireCtaClicked('connectCalendar');
		if (fg('post-office_enable_semantic-event-fixes')) {
			onCtaActioned({ interactionIdentifier: 'connect-calendar' });
		} else {
			void messageInstanceClicked(getPostOfficeDismissEvent('connect-calendar', 'primary'));
		}
		openLoomMeetingRecordings({
			bridge: 'loom-meeting-nudge-sidenav',
			cloudId,
			interactionSource: 'confluence',
			loomSource: 'confluence-nudge-sidenav',
		});
		finishNudgeFlow();
	};

	const handleRemindMe = () => {
		fireCtaClicked('remindMe');
		void messageInstanceClicked(getPostOfficeDismissEvent('remind-me'));
		requestLoomMeetingNudgeReminder();
		void flags.showFlag({
			id: 'loom-meeting-nudge-reminder-confirmation',
			type: 'success-circle',
			title: reminderConfirmation.title,
			description: reminderConfirmation.description,
			customComponent: AutoDismissFlag,
			close: 'never',
			onShown: () => fireScreenSeen('remindMe'),
			onClose: () => fireFlagDismissed('flagRemindMe'),
		});
		finishNudgeFlow();
	};

	const handleNotInterestedConfirmationDismissed = () => {
		if (fg('post-office_enable_semantic-event-fixes')) {
			onDeclined({ interactionIdentifier: 'not-interested' });
		} else {
			void messageInstanceClicked(getPostOfficeDismissEvent('not-interested'));
		}
		fireFlagDismissed('flagNotInterested');
		finishNudgeFlow();
	};

	const handleNotInterestedUndo = () => {
		void flags.hideFlag(NOT_INTERESTED_FLAG_ID);
		fireCtaClicked('undo');
		finishNudgeFlow();
	};

	const handleNotInterested = () => {
		fireCtaClicked('notInterested');
		setIsNotInterestedFlagOpen(true);
		void flags.showFlag({
			id: NOT_INTERESTED_FLAG_ID,
			type: 'success-circle',
			title: notInterestedConfirmation.title,
			description: notInterestedConfirmation.description,
			actions: [
				{
					content: notInterestedConfirmation.undo,
					onClick: handleNotInterestedUndo,
				},
			],
			customComponent: AutoDismissFlag,
			close: 'never',
			onShown: () => fireScreenSeen('notInterested'),
			onClose: handleNotInterestedConfirmationDismissed,
		});
	};

	const handleWhyAmISeeingThis = () => {
		fireCtaClicked('whyAmISeeingThis');
		setIsWhyModalOpen(true);
	};

	const handleCloseWhyAmISeeingThis = () => {
		setIsWhyModalOpen(false);
	};

	return (
		<>
			{!isNotInterestedFlagOpen && (
				<Box xcss={styles.container}>
					<Stack space="space.100">
						<Inline alignBlock="start" spread="space-between">
							<Box xcss={styles.aiNotesIcon}>
								<Image
									src={AiNotesIcon}
									alt=""
									width="40"
									height="40"
									testId="loom-meeting-nudge-ai-notes-icon"
								/>
							</Box>
							<DropdownMenu
								trigger={({ triggerRef, onClick, ...triggerProps }) => (
									// eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions
									<IconButton
										{...triggerProps}
										appearance="subtle"
										icon={ShowMoreHorizontalIcon}
										label={intl.formatMessage(registeredContent.default.moreActions)}
										onClick={(event) => {
											fireCtaClicked('more');
											onClick?.(event);
										}}
										ref={triggerRef}
										spacing="compact"
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem onClick={handleNotInterested}>
										<FormattedMessage {...registeredContent.default.notInterested} />
									</DropdownItem>
									<DropdownItem onClick={handleWhyAmISeeingThis}>
										<FormattedMessage {...registeredContent.default.whyAmISeeingThis} />
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</Inline>

						<Heading color="color.text" size="xsmall">
							<FormattedMessage {...registeredContent.default.heading} />
						</Heading>
						<Text as="p" color="color.text.subtle">
							<FormattedMessage {...registeredContent.default.body} />
						</Text>
						<Inline alignBlock="center" space="space.100">
							<Pressable onClick={handleConnectCalendar} xcss={styles.action}>
								<Text as="p" color="color.link">
									<FormattedMessage {...registeredContent.default.connectCalendar} />
								</Text>
							</Pressable>
							<span aria-hidden="true">·</span>
							<Pressable onClick={handleRemindMe} xcss={styles.action}>
								<Text as="p" color="color.link">
									<FormattedMessage {...registeredContent.default.remindMe} />
								</Text>
							</Pressable>
						</Inline>
					</Stack>
				</Box>
			)}
			<ModalTransition>
				{isWhyModalOpen && (
					<ModalDialog onClose={handleCloseWhyAmISeeingThis} width="large">
						<ModalHeader>
							<ModalTitle>
								<FormattedMessage {...registeredContent.whyModal.title} />
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Text as="p">
								<FormattedMessage
									{...registeredContent.whyModal.introduction}
									values={{
										strong: (chunks) => <Text weight="bold">{chunks}</Text>,
									}}
								/>
							</Text>
							<ul>
								<li>
									<FormattedMessage {...registeredContent.whyModal.teamworkCollection} />
								</li>
								<li>
									<FormattedMessage {...registeredContent.whyModal.atlassianTools} />
								</li>
								<li>
									<FormattedMessage {...registeredContent.whyModal.meetingWorkflow} />
								</li>
							</ul>
							<Box xcss={styles.privacyLinkContainer}>
								<Link href={DATA_PRIVACY_URL} target="_blank">
									<FormattedMessage {...registeredContent.whyModal.privacyLink} />
								</Link>
							</Box>
						</ModalBody>
						<ModalFooter>
							{/* eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions */}
							<Button appearance="primary" onClick={handleCloseWhyAmISeeingThis}>
								<FormattedMessage {...registeredContent.whyModal.close} />
							</Button>
						</ModalFooter>
					</ModalDialog>
				)}
			</ModalTransition>
		</>
	);
};
